/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import React, { useMemo, useState } from 'react';
import { Card } from '@g17eco/atoms/card';
import { Input, Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import type { InsightSynthesis, Recommendation } from '@g17eco/types/insights-synthesis';
import { RecommendationRow } from './partials/RecommendationRow';
import { InsightViewModal } from './partials/InsightViewModal';
import { Chart } from 'react-google-charts';

enum RecommendationColour {
  Critical = '#dc3545',
  High = '#fd7e14',
  Medium = '#ffc107',
  Low = '#28a745',
  None = '#6c757d',
}

interface Props {
  insights: InsightSynthesis[];
}

interface ActionWithSource extends Recommendation {
  insightId: string;
  insightTitle: string;
  insightPriority: 'low' | 'medium' | 'high' | 'critical';
  insightPriorityScore: number;
  insight: InsightSynthesis;
}

export const RecommendationsView: React.FC<Props> = ({ insights }) => {
  const [selectedAction, setSelectedAction] = useState<ActionWithSource | null>(null);
  const [highImpactPage, setHighImpactPage] = useState(1);
  const [priorityPage, setPriorityPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const itemsPerPageOptions = [5, 10, 25, 50] as const;

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    setItemsPerPage(value);
    setHighImpactPage(1);
    setPriorityPage(1);
  };

  // Extract all recommendations from insights
  const actions = useMemo(() => {
    const allActions: ActionWithSource[] = [];
    insights.forEach((insight) => {
      insight.content.recommendations.forEach((rec) => {
        allActions.push({
          ...rec,
          insightId: insight._id,
          insightTitle: insight.content.title,
          insightPriority: insight.content.impact,
          insightPriorityScore: insight.content.priorityScore,
          insight: insight,
        });
      });
    });
    // Sort all actions by priority score
    return allActions.sort((a, b) => b.insightPriorityScore - a.insightPriorityScore);
  }, [insights]);

  // High Impact Actions with pagination
  const { items: highestImpact, totalPages: highImpactTotalPages } = useMemo(() => {
    const sorted = [...actions].sort((a, b) => (b.impactLevel || 0) - (a.impactLevel || 0));
    const startIndex = (highImpactPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return {
      items: sorted.slice(startIndex, endIndex),
      totalPages: Math.ceil(sorted.length / itemsPerPage),
      total: sorted.length,
    };
  }, [actions, highImpactPage, itemsPerPage]);

  // Priority Actions with pagination
  const { items: priorityActions, totalPages: priorityTotalPages } = useMemo(() => {
    const priorityOrder: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
    const sorted = [...actions].sort((a, b) => {
      const aPriority = priorityOrder[a.insightPriority] || 0;
      const bPriority = priorityOrder[b.insightPriority] || 0;
      return bPriority - aPriority;
    });
    const startIndex = (priorityPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return {
      items: sorted.slice(startIndex, endIndex),
      totalPages: Math.ceil(sorted.length / itemsPerPage),
      total: sorted.length,
    };
  }, [actions, priorityPage, itemsPerPage]);

  const handleHighImpactPageChange = (page: number) => {
    setHighImpactPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePriorityPageChange = (page: number) => {
    setPriorityPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Prepare chart data for Impact vs Effort (single-series format, one row per point - matches PrioritiesView)
  const impactVsEffortData = useMemo(() => {
    const priorityColorMap: Record<string, string> = {
      critical: RecommendationColour.Critical,
      high: RecommendationColour.High,
      medium: RecommendationColour.Medium,
      low: RecommendationColour.Low,
    };

    const data: Array<Array<unknown>> = [
      ['Effort', 'Impact', { role: 'style' }, { role: 'tooltip' }],
    ];

    actions.forEach((action) => {
      const effort = Number(action.effortRequired) || 5;
      const impact = Number(action.impactLevel) || 5;
      const priority = (action.insightPriority || 'medium').toLowerCase();
      const color = priorityColorMap[priority] || RecommendationColour.None;
      const tooltip = `${action.title}\nImpact: ${impact}\nEffort: ${effort}\nPriority: ${action.insightPriority}`;
      data.push([effort, impact, `point { fill-color: ${color}; size: 8; opacity: 0.7; }`, tooltip]);
    });

    return data;
  }, [actions]);

  // Prepare chart data for Priority vs Effort (single-series format, one row per point - matches PrioritiesView)
  const priorityVsEffortData = useMemo(() => {
    const priorityColorMap: Record<string, string> = {
      critical: RecommendationColour.Critical,
      high: RecommendationColour.High,
      medium: RecommendationColour.Medium,
      low: RecommendationColour.Low,
    };

    const priorityScoreMap: Record<string, number> = {
      critical: 10,
      high: 7.5,
      medium: 5,
      low: 2.5,
    };

    const data: Array<Array<unknown>> = [
      ['Effort', 'Priority', { role: 'style' }, { role: 'tooltip' }],
    ];

    actions.forEach((action) => {
      const effort = Number(action.effortRequired) || 5;
      const priority = (action.insightPriority || 'medium').toLowerCase();
      const priorityScore = priorityScoreMap[priority] ?? 5;
      const color = priorityColorMap[priority] || RecommendationColour.None;
      const tooltip = `${action.title}\nPriority: ${action.insightPriority}\nEffort: ${effort}`;
      data.push([effort, priorityScore, `point { fill-color: ${color}; size: 8; opacity: 0.7; }`, tooltip]);
    });

    return data;
  }, [actions]);

  if (actions.length === 0) {
    return (
      <div className='text-center py-5'>
        <i className='fas fa-tasks fa-3x text-ThemeTextLight mb-3' />
        <h4 className='text-ThemeTextDark mb-2'>No Recommendations Available</h4>
        <p className='text-ThemeTextMedium'>
          Recommendations will appear here once insights with recommendations are generated.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Quick Access Charts */}
      <div className='mb-2 p-3 background-ThemeBgExtralight border shadow-sm'>
        <div className='row'>
          <div className='col-md-6 mb-md-0'>
            <h6 className='strong text-ThemeTextDark mb-1'><i className='fa fa-bullseye text-ThemeTextMedium me-2' /> Impact vs. Effort</h6>
            <p className='text-sm text-ThemeTextMedium mb-1'>
              Focus on upper-left quadrant (high impact, low effort)
            </p>
            <Chart
              chartVersion={'51' as 'current'}
              chartType='ScatterChart'
              width='100%'
              height='200px'
              data={impactVsEffortData}
              options={{
                hAxis: {
                  title: 'Effort Required',
                  minValue: 0,
                  maxValue: 10,
                },
                vAxis: {
                  title: 'Business Impact',
                  minValue: 0,
                  maxValue: 10,
                },
                legend: { position: 'none' },
                chartArea: { width: '90%', height: '80%' },
                tooltip: { isHtml: false, trigger: 'both' },
              }}
              chartEvents={[
                {
                  eventName: 'select',
                  callback({ chartWrapper }) {
                    const chart = chartWrapper.getChart();
                    const selection = chart.getSelection();
                    if (selection && selection.length > 0) {
                      const selectedRow = selection[0].row;
                      if (selectedRow !== null && selectedRow !== undefined) {
                        setSelectedAction(actions[selectedRow]);
                      }
                    }
                  },
                },
              ]}
            />
          </div>
          <div className='col-md-6'>
            <h6 className='strong text-ThemeTextDark mb-1'><i className='fa fa-exclamation-circle text-ThemeTextMedium me-2' /> Priority vs. Effort</h6>
            <p className='text-sm text-ThemeTextMedium mb-1'>
              Identify critical priorities that require lower effort
            </p>
            <Chart
              chartVersion={'51' as 'current'}
              chartType='ScatterChart'
              width='100%'
              height='200px'
              data={priorityVsEffortData}
              options={{
                hAxis: {
                  title: 'Effort Required',
                  minValue: 0,
                  maxValue: 10,
                },
                vAxis: {
                  title: 'Priority Level',
                  minValue: 0,
                  maxValue: 10,
                },
                legend: { position: 'none' },
                chartArea: { width: '90%', height: '80%' },
                tooltip: { isHtml: false, trigger: 'both' },
              }}
              chartEvents={[
                {
                  eventName: 'select',
                  callback({ chartWrapper }) {
                    const chart = chartWrapper.getChart();
                    const selection = chart.getSelection();
                    if (selection && selection.length > 0) {
                      const selectedRow = selection[0].row;
                      if (selectedRow !== null && selectedRow !== undefined) {
                        setSelectedAction(actions[selectedRow]);
                      }
                    }
                  },
                },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Highest Impact Section */}
      <div className='py-3 border-bottom'>
        <div className='d-flex gap-2'>
          <div>
            <span className='badge background-ThemeAccentExtralight p-2 border-0'>
              <i className='text-ThemeAccentDark fal fa-2x fa-chart-line' />
            </span>
          </div>
          <div className='d-flex flex-column ms-2'>
            <div className='text-xl strong'>Highest Impact</div>
            <div className='text-ThemeTextMedium text-md'>
              Greatest business value potential ({actions.length} recommendations)
            </div>
          </div>
        </div>
      </div>
      <Card className='mb-4' >
        <div className='p-3'>
          {highestImpact.map((action, index) => (
            <RecommendationRow
              key={`hi-${index}`}
              action={action}
              index={(highImpactPage - 1) * itemsPerPage + index + 1}
              colors={{ number: 'info' }}
              onViewInsight={() => setSelectedAction(action)}
            />
          ))}
        </div>
        {highImpactTotalPages > 1 && (
          <div className='p-3 border-top d-flex justify-content-between align-items-center'>

          {/* Items per page selector */}
          <div className='d-flex justify-content-end align-items-center mb-2'>
            <label htmlFor='items-per-page' className='text-ThemeTextMedium me-2'>
              Items per page:
            </label>
            <Input
              id='items-per-page'
              type='select'
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className='w-auto'
            >
              {itemsPerPageOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Input>
          </div>

            <div className='text-sm text-ThemeTextMedium'>
              Page {highImpactPage} of {highImpactTotalPages}
            </div>
            <Pagination className='mb-0'>
              <PaginationItem disabled={highImpactPage === 1}>
                <PaginationLink previous onClick={() => handleHighImpactPageChange(highImpactPage - 1)} />
              </PaginationItem>
              {[...Array(highImpactTotalPages)].map((_, i) => {
                const page = i + 1;
                if (
                  page === 1 ||
                  page === highImpactTotalPages ||
                  (page >= highImpactPage - 1 && page <= highImpactPage + 1)
                ) {
                  return (
                    <PaginationItem key={page} active={page === highImpactPage}>
                      <PaginationLink onClick={() => handleHighImpactPageChange(page)}>{page}</PaginationLink>
                    </PaginationItem>
                  );
                } else if (page === highImpactPage - 2 || page === highImpactPage + 2) {
                  return (
                    <PaginationItem key={page} disabled>
                      <PaginationLink>...</PaginationLink>
                    </PaginationItem>
                  );
                }
                return null;
              })}
              <PaginationItem disabled={highImpactPage === highImpactTotalPages}>
                <PaginationLink next onClick={() => handleHighImpactPageChange(highImpactPage + 1)} />
              </PaginationItem>
            </Pagination>
          </div>
        )}
      </Card>

      {/* Priority Recommendations Section */}
      <div className='py-3 border-bottom'>
        <div className='d-flex gap-2'>
          <div>
            <span className='badge background-ThemeDangerExtralight p-2 border-0'>
              <i className='text-ThemeDangerDark fal fa-2x fa-exclamation-circle' />
            </span>
          </div>
          <div className='d-flex flex-column ms-2'>
            <div className='text-xl strong'>Priority Recommendations</div>
            <div className='text-ThemeTextMedium text-md'>
              Critical and high priority recommendations requiring attention ({actions.length} recommendations)
            </div>
          </div>
        </div>
      </div>
      <Card className='mb-4'>
        <div className='p-3'>
          {priorityActions.map((action, index) => (
            <RecommendationRow
              key={`pa-${index}`}
              action={action}
              index={(priorityPage - 1) * itemsPerPage + index + 1}
              colors={{ number: 'danger' }}
              onViewInsight={() => setSelectedAction(action)}
            />
          ))}
        </div>
        {priorityTotalPages > 1 && (
          <div className='p-3 border-top d-flex justify-content-between align-items-center'>

          {/* Items per page selector */}
          <div className='d-flex justify-content-end align-items-center mb-2'>
            <label htmlFor='items-per-page' className='text-ThemeTextMedium me-2'>
              Items per page:
            </label>
            <Input
              id='items-per-page'
              type='select'
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className='w-auto'
            >
              {itemsPerPageOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Input>
          </div>

            <div className='text-sm text-ThemeTextMedium'>
              Page {priorityPage} of {priorityTotalPages}
            </div>
            <Pagination className='mb-0'>
              <PaginationItem disabled={priorityPage === 1}>
                <PaginationLink previous onClick={() => handlePriorityPageChange(priorityPage - 1)} />
              </PaginationItem>
              {[...Array(priorityTotalPages)].map((_, i) => {
                const page = i + 1;
                if (
                  page === 1 ||
                  page === priorityTotalPages ||
                  (page >= priorityPage - 1 && page <= priorityPage + 1)
                ) {
                  return (
                    <PaginationItem key={page} active={page === priorityPage}>
                      <PaginationLink onClick={() => handlePriorityPageChange(page)}>{page}</PaginationLink>
                    </PaginationItem>
                  );
                } else if (page === priorityPage - 2 || page === priorityPage + 2) {
                  return (
                    <PaginationItem key={page} disabled>
                      <PaginationLink>...</PaginationLink>
                    </PaginationItem>
                  );
                }
                return null;
              })}
              <PaginationItem disabled={priorityPage === priorityTotalPages}>
                <PaginationLink next onClick={() => handlePriorityPageChange(priorityPage + 1)} />
              </PaginationItem>
            </Pagination>
          </div>
        )}
      </Card>

      {/* Recommendation Detail Modal */}
      {selectedAction && (
        <InsightViewModal
          insight={selectedAction.insight}
          isOpen={!!selectedAction}
          onClose={() => setSelectedAction(null)}
          highlightedActionIndex={selectedAction.insight.content.recommendations.findIndex(
            (rec) => rec.title === selectedAction.title
          )}
        />
      )}
    </div>
  );
};

